import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFeatureFlagEnabled, useFeatureFlagPayload } from 'posthog-js/react';
import { JsonType } from 'posthog-js';

/**
 * Hook that wraps useFeatureFlagEnabled and adds query parameter overrides.
 *
 * Query parameters:
 * - featureFlagsOn=flag1,flag2 - if the flag name exists in this list, always return true
 * - featureFlagsOff=flag1,flag2 - if the flag name exists in this list, always return false
 *
 * @param flagName - The name of the feature flag to check
 * @returns The feature flag value, potentially overridden by query parameters
 */
export const useFeatureFlag = (flagName: string): { value: boolean | undefined; variantData: JsonType | undefined } => {
  const [searchParams] = useSearchParams();
  const originalValue = useFeatureFlagEnabled(flagName);
  const variantData = useFeatureFlagPayload(flagName);
  return useMemo(() => {
    // Check if flag is in featureFlagsOn query param
    const flagsOn = searchParams.get('featureFlagsOn');
    if (flagsOn) {
      const flagsOnArray = flagsOn.split(',').map((flag) => flag.trim());
      if (flagsOnArray.includes(flagName)) {
        return { value: true, variantData };
      }
    }

    // Check if flag is in featureFlagsOff query param
    const flagsOff = searchParams.get('featureFlagsOff');
    if (flagsOff) {
      const flagsOffArray = flagsOff.split(',').map((flag) => flag.trim());
      if (flagsOffArray.includes(flagName)) {
        return { value: false, variantData };
      }
    }

    // Return original value if no override
    return {
      value: originalValue,
      variantData,
    };
  }, [flagName, searchParams, originalValue, variantData]);
};
